import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCircles,
  fetchCirclesOptions,
  fetchCirclesContacts,
} from './actions';
import { PAGINATION } from '@/constants/pagination';

const initialState = {
  isLoading: false,

  companies: [],
  companiesPagination: {},

  circlesContactsList: [],
  circlesContactsListloading: false,
  circlesContactsListPagination: {},

  isOptionsLoading: true,
  options: [],

  sort: {
    element: null,
    order: null,
  },

  circlesContactsListSort: {
    element: null,
    order: null,
  },

  page: {
    number: 0,
    offset: 0,
    limit: PAGINATION.CONTACTS,
  },

  circlesContactsListPage: {
    number: 0,
    offset: 0,
    limit: 50,
  },

  filters: {
    circleChanged: false,
    user: [{ id: 'org' }],
    company: [],
    location: [],
    industry: [],
    employeeRange: [],
    jobTitle: [],
    tags: [],
    skills: [],
    pastCompany: [],
  },
  ignoreId: 0,

  selectedCandidates: [],
};

export const circlesSlice = createSlice({
  name: 'circles',
  initialState,
  reducers: {
    applyFilter: (state, action) => {
      const { type, selected = [], circleChanged = true } = action?.payload;
      state.page = { ...initialState?.page };
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.companiesPagination = {};
      state.circlesContactsListPagination = {};
      state.filters.circleChanged = circleChanged;
      state.filters[type] = selected;
      if (type === 'company') state.filters.pastCompany = [];
      if (type === 'pastCompany') state.filters.company = [];
    },

    clearFilter: (state, action) => {
      const { type, isMulti = false } = action?.payload;
      state.page = { ...initialState?.page };
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.filters[type] = isMulti ? [] : null;
      if (type === 'user') {
        state.ignoreId = initialState?.ignoreId;
        state.filters.user = initialState?.filters?.user;
      }
    },

    applyAllFilter: (state, action) => {
      state.page = { ...initialState?.page };
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.companiesPagination = {};
      state.circlesContactsListPagination = {};
      state.filters.circleChanged = true;
      state.filters['company'] = action?.payload?.company;
      state.filters['location'] = action?.payload?.location;
      state.filters['industry'] = action?.payload?.industry;
      state.filters['employeeRange'] = action?.payload?.employeeRange;
      state.filters['operatingStatus'] = action?.payload?.operatingStatus;
      state.filters['jobTitle'] = action?.payload?.jobTitle;
      state.filters['tags'] = action?.payload?.tags;
      state.filters['skills'] = action?.payload?.skills;
    },

    clearAllFilter: (state, action) => {
      state.page = { ...initialState?.page };
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.companiesPagination = {};
      state.circlesContactsListPagination = {};
      state.ignoreId = initialState?.ignoreId;
      state.filters = {
        ...initialState?.filters,
        circleChanged: false,
      };
    },

    applySort: (state, action) => {
      const { element = 'c_c', order = 'desc' } = action?.payload;
      state.page = { ...initialState?.page };
      state.sort.element = element;
      state.sort.order = order;
    },

    applyCirclesContactsSort: (state, action) => {
      const { element = 'name', order = 'asc' } = action?.payload;
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.circlesContactsListSort.element = element;
      state.circlesContactsListSort.order = order;
    },

    applySecondDegreeConnection: (state, action) => {
      const { ignoreId } = action?.payload;
      state.ignoreId = ignoreId;
      state.page = { ...initialState?.page };
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
      state.companiesPagination = {};
      state.circlesContactsListPagination = {};
    },

    applyPage: (state) => {
      state.page.number = state?.page?.number + 1;
      state.page.offset = state?.page?.number * state?.page?.limit;
    },

    resetPage: (state) => {
      state.page = { ...initialState?.page };
    },

    applyCirclesContactsListPage: (state, { payload }) => {
      state.circlesContactsListPage.number = payload?.page;
      state.circlesContactsListPage.offset =
        state?.circlesContactsListPage?.number *
        state?.circlesContactsListPage?.limit;
    },

    resetCirclesContactsListPage: (state) => {
      state.circlesContactsListPage = {
        ...initialState?.circlesContactsListPage,
      };
    },

    addCandidateToSelected: (state, action) => {
      const { candidate } = action?.payload;
      state?.selectedCandidates?.push(candidate);
    },

    removeConnectedUsersFromCandidate: (state, action) => {
      const { candidateId, removeConnectedUsers = [], index } = action.payload;
      const beforeCandidates = state?.selectedCandidates?.slice(0, index);
      const selectedCandidate = state?.selectedCandidates?.find(
        (candidate) => candidate.id === candidateId
      );
      selectedCandidate.connected_users =
        selectedCandidate?.connected_users?.filter(
          (conUser) => !removeConnectedUsers?.includes(conUser?.id)
        );
      const afterCandidates = state?.selectedCandidates?.slice(index + 1);
      state.selectedCandidates = [
        ...beforeCandidates,
        selectedCandidate,
        ...afterCandidates,
      ];
    },

    removeCandidateFromSelected: (state, action) => {
      const { candidateId } = action?.payload;
      state.selectedCandidates = state?.selectedCandidates?.filter(
        (candidate) => candidate?.id !== candidateId
      );
    },

    addCandidatesToSelectedList: (state, action) => {
      const { selectedCandidates = [] } = action?.payload;
      const alreadySelectedCandidatesIds = state?.selectedCandidates?.map(
        (candidate) => candidate?.id
      );
      selectedCandidates?.forEach((candidate) => {
        if (!alreadySelectedCandidatesIds?.includes(candidate?.id)) {
          state?.selectedCandidates?.push(candidate);
        }
      });
    },

    removeCandidatesToSelectedList: (state, action) => {
      const { selectedCandidates = [] } = action?.payload;
      const selectedCandidatesIds = selectedCandidates?.map(
        (candidate) => candidate?.id
      );
      state.selectedCandidates = state?.selectedCandidates?.filter(
        (candidate) => !selectedCandidatesIds?.includes(candidate?.id)
      );
    },

    removeMyConnAndSingleMutualConnCandidates: (state) => {
      state.selectedCandidates = state?.selectedCandidates?.filter(
        (candidate) =>
          candidate?.connected_users?.length > 1 && !candidate?.my_circle
      );
    },

    removeAllcandidatesFromSelected: (state) => {
      state.selectedCandidates = [];
    },
  },

  extraReducers: {
    [fetchCircles.pending]: (state) => {
      state.isLoading = true;
    },
    [fetchCircles.fulfilled]: (state, { payload }) => {
      state.isLoading = false;

      if (state.page.number === 0) {
        state.companies = payload?.data?.company || [];
        return;
      }
      if (payload?.data?.company?.length) {
        state.companies = [...state.companies, ...payload?.data?.company];
      }
      state.companiesPagination = payload?.data?.pagination;
    },
    [fetchCirclesContacts.pending]: (state) => {
      state.circlesContactsListloading = true;
    },
    [fetchCirclesContacts.fulfilled]: (state, { payload }) => {
      state.circlesContactsListloading = false;
      state.circlesContactsListPagination = payload?.data?.pagination;

      state.circlesContactsList = payload?.data?.contact || [];
    },
    [fetchCirclesOptions.pending]: (state) => {
      state.isOptionsLoading = true;
    },
    [fetchCirclesOptions.fulfilled]: (state, { payload }) => {
      state.isOptionsLoading = false;
      state.options = payload?.data?.options || [];
    },
  },
});

export const {
  applyFilter,
  clearFilter,
  applyAllFilter,
  clearAllFilter,
  applySort,
  applyCirclesContactsSort,
  applyPage,
  resetPage,
  applySecondDegreeConnection,
  removeCandidateFromSelected,
  addCandidateToSelected,
  removeAllcandidatesFromSelected,
  removeConnectedUsersFromCandidate,
  removeMyConnAndSingleMutualConnCandidates,
  resetCirclesContactsListPage,
  applyCirclesContactsListPage,
  addCandidatesToSelectedList,
  removeCandidatesToSelectedList,
} = circlesSlice.actions;
export default circlesSlice.reducer;
